import React from 'react';
import {
  HiOutlinePhotograph,
  HiOutlineFilm,
  HiOutlineDocumentText,
  HiOutlineLightningBolt,
  HiOutlineAdjustments,
  HiOutlineCloudUpload,
  HiOutlineCode,
  HiOutlinePuzzle,
  HiOutlineTemplate,
  HiOutlineUserGroup,
  HiOutlineCreditCard,
  HiOutlineShieldCheck,
  HiOutlineChartBar,
  HiOutlineServer,
} from 'react-icons/hi';
import './Features.css';

const featureSections = [
  {
    id: 'core',
    label: 'Core Engine',
    title: 'Powerful Enhancement Engine',
    subtitle: 'The heart of AIScaler — advanced neural networks for every media type.',
    features: [
      {
        icon: <HiOutlinePhotograph />,
        title: 'Image Processing',
        items: [
          'AI Image Upscaling (2x, 4x, 8x)',
          'Noise Reduction & Deblurring',
          'Face Restoration (auto detect faces)',
          'Detail Enhancement (textures, edges)',
          'Color Correction & HDR enhancement',
          'Old photo restoration (scratches, fading)',
        ],
      },
      {
        icon: <HiOutlineFilm />,
        title: 'Video Enhancement',
        items: [
          'Video Upscaling (to 1080p / 4K / 8K)',
          'Frame Interpolation (30fps → 60fps / 120fps)',
          'Stabilization',
          'Motion blur reduction',
          'Low-light enhancement',
          'Compression artifact removal',
        ],
      },
      {
        icon: <HiOutlineDocumentText />,
        title: 'Document & Text Enhancement',
        items: [
          'Scan cleanup (remove noise, shadows)',
          'Text sharpening for OCR readiness',
          'PDF enhancement',
          'Handwritten text clarity improvement',
          'Auto straighten & crop',
        ],
      },
    ],
  },
  {
    id: 'modes',
    label: 'Smart Modes',
    title: 'Intelligent AI Modes',
    subtitle: 'Auto-detect content and apply the perfect enhancement profile.',
    features: [
      {
        icon: <HiOutlineLightningBolt />,
        title: 'Auto-Detect Content Type',
        items: ['Face', 'Product', 'Document', 'Landscape', 'Video'],
      },
      {
        icon: <HiOutlineTemplate />,
        title: 'Pre-Built Modes',
        items: [
          'E-commerce Mode (sharp edges + clean background)',
          'Portrait Mode (skin + face restore)',
          'Old Photo Restore',
          'Document Mode (OCR ready)',
          'Video Creator Mode',
        ],
      },
      {
        icon: <HiOutlineAdjustments />,
        title: 'Custom Tuning Sliders',
        items: ['Sharpness', 'Noise', 'Detail recovery', 'Color intensity'],
      },
    ],
  },
  {
    id: 'batch',
    label: 'Automation',
    title: 'Batch & Workflow Automation',
    subtitle: 'Process thousands of files with automated workflows.',
    features: [
      {
        icon: <HiOutlineCloudUpload />,
        title: 'Batch Processing',
        items: [
          'Bulk upload (1000+ files)',
          'Folder-based processing',
          'Queue system with progress tracking',
        ],
      },
      {
        icon: <HiOutlineLightningBolt />,
        title: 'Workflow Automation',
        items: [
          'Preset workflows: Upload → Enhance → Resize → Export',
          'Scheduled jobs (run later)',
          'Auto-processing via webhook triggers',
        ],
      },
    ],
  },
  {
    id: 'api',
    label: 'Developer Platform',
    title: 'API & Developer Platform',
    subtitle: 'Integrate AI enhancement directly into your applications.',
    features: [
      {
        icon: <HiOutlineCode />,
        title: 'REST API',
        items: [
          'Image enhancement endpoints',
          'Video processing endpoints',
          'Document cleanup endpoints',
        ],
      },
      {
        icon: <HiOutlineCode />,
        title: 'SDKs & Tooling',
        items: [
          'Node.js SDK',
          'Python SDK',
          'Webhooks for job completion',
        ],
      },
      {
        icon: <HiOutlineChartBar />,
        title: 'API Dashboard',
        items: [
          'Usage tracking',
          'Logs & error monitoring',
          'Rate limit management',
        ],
      },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    title: 'Seamless Integrations',
    subtitle: 'Connect AIScaler with your favorite tools and platforms.',
    features: [
      {
        icon: <HiOutlinePuzzle />,
        title: 'Platform Plugins',
        items: [
          'Shopify plugin (auto enhance product images)',
          'Canva extension',
          'Figma plugin',
        ],
      },
      {
        icon: <HiOutlineCloudUpload />,
        title: 'Cloud & Automation',
        items: [
          'Google Drive / Dropbox import',
          'Zapier integration',
        ],
      },
    ],
  },
  {
    id: 'ux',
    label: 'User Experience',
    title: 'Delightful UX Features',
    subtitle: 'Intuitive tools that make AI enhancement effortless.',
    features: [
      {
        icon: <HiOutlineTemplate />,
        title: 'Upload & Preview',
        items: [
          'Drag & drop upload',
          'Before/After slider preview',
          'Side-by-side comparison',
          'Real-time preview (low-res preview first)',
        ],
      },
      {
        icon: <HiOutlinePhotograph />,
        title: 'Export Options',
        items: [
          'JPG, PNG, WebP, MP4, PDF formats',
          'Social media presets',
          'Print-ready presets',
          'Web-optimized presets',
        ],
      },
    ],
  },
  {
    id: 'team',
    label: 'Collaboration',
    title: 'Team & Collaboration',
    subtitle: 'Work together with role-based access and shared assets.',
    features: [
      {
        icon: <HiOutlineUserGroup />,
        title: 'Team Features',
        items: [
          'Team accounts',
          'Role-based access (admin, editor, viewer)',
          'Shared asset library',
          'Activity logs',
        ],
      },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    title: 'Flexible Billing & Monetization',
    subtitle: 'Transparent pricing that scales with your usage.',
    features: [
      {
        icon: <HiOutlineCreditCard />,
        title: 'Plans & Billing',
        items: [
          'Free (limited credits), Pro, and Business plans',
          'Credit-based usage system',
          'API usage billing',
          'Stripe integration',
        ],
      },
    ],
  },
  {
    id: 'security',
    label: 'Security',
    title: 'Security & Compliance',
    subtitle: 'Enterprise-grade security for your sensitive content.',
    features: [
      {
        icon: <HiOutlineShieldCheck />,
        title: 'Data Protection',
        items: [
          'Secure file storage (S3 or equivalent)',
          'Auto-delete files after X days',
          'Private processing (no training on user data)',
          'GDPR-ready',
        ],
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    title: 'Analytics Dashboard',
    subtitle: 'Track performance and measure the impact of AI enhancement.',
    features: [
      {
        icon: <HiOutlineChartBar />,
        title: 'Insights & Metrics',
        items: [
          'Processing stats',
          'Quality improvement metrics',
          'API usage charts',
          'Time saved insights',
        ],
      },
    ],
  },
  {
    id: 'infra',
    label: 'Infrastructure',
    title: 'Performance & Infrastructure',
    subtitle: 'Built for speed and reliability at any scale.',
    features: [
      {
        icon: <HiOutlineServer />,
        title: 'Backend Power',
        items: [
          'GPU worker system (queue-based)',
          'Async job processing',
          'CDN delivery for outputs',
          'Caching for repeated jobs',
          'Multi-region support',
        ],
      },
    ],
  },
];

export default function Features() {
  return (
    <div className="features-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-label">Features</span>
          <h1 className="section-title">Everything AIScaler Can Do</h1>
          <p className="section-subtitle">
            A comprehensive AI-powered platform for upscaling and enhancing images, videos, and documents.
          </p>
        </div>
      </section>

      {/* Feature nav */}
      <div className="features-nav-wrap">
        <nav className="container features-nav">
          {featureSections.map(({ id, label }) => (
            <a key={id} href={`#${id}`} className="features-nav__link">{label}</a>
          ))}
        </nav>
      </div>

      {/* Feature sections */}
      {featureSections.map(({ id, label, title, subtitle, features }, sIdx) => (
        <section key={id} id={id} className={`section ${sIdx % 2 === 1 ? 'features-section--alt' : ''}`}>
          <div className="container">
            <div className="section-header">
              <span className="section-label">{label}</span>
              <h2 className="section-title">{title}</h2>
              <p className="section-subtitle">{subtitle}</p>
            </div>
            <div className={`features-grid features-grid--${features.length}`}>
              {features.map(({ icon, title: fTitle, items }) => (
                <div key={fTitle} className="card feature-card">
                  <div className="feature-card__icon">{icon}</div>
                  <h3 className="feature-card__title">{fTitle}</h3>
                  <ul className="feature-card__list">
                    {items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
